import { EarningsItemWithEntity } from '@api';
import { Alert, AlertType } from '@design/components/alert/Alert';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { WithNAFallback } from '@design/components/table';
import { getPriceStatus, PriceStatus } from '@helpers';
import { useEarningsScenario } from '@terminal/react-query-hooks/earnings/useEarningsScenario';
import { useEntity } from '@terminal/react-query-hooks/entities/useEntities';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { SummaryTable } from '../summary-table/SummaryTable';
import { formatNumber } from '../utils/number-formatter';
import styles from './EarningsEventDrawerContent.module.scss';
import { ChartError } from './earnings-chart/chart-error/ChartError';
import { EarningsWidget } from './earnings-widget/EarningsWidget';

const surpriseClassMap: Record<string, string | undefined> = {
  [PriceStatus.Positive]: styles.positive,
  [PriceStatus.Negative]: styles.negative,
  [PriceStatus.Default]: undefined,
};

export type EarningsEventDrawerContentProps = {
  data: EarningsItemWithEntity;
  source: PosthogTrackingSource;
};

export const EarningsEventDrawerContent = ({
  data,
  source,
}: EarningsEventDrawerContentProps) => {
  const { t } = useTranslation('calendar');
  const { data: entityData } = useEntity(data.entity_tag);

  const {
    fiscal_period,
    fiscal_year,
    currency,
    eps,
    revenue,
    exchange,
    reporting_date,
  } = data;
  const {
    value: epsValue,
    estimate: epsEstimate,
    surprise: epsSurprise,
    surprise_percentage: epsSurprisePercentage,
  } = eps;
  const {
    value: revenueValue,
    estimate: revenueEstimate,
    surprise: revenueSurprise,
    surprise_percentage: revenueSurprisePercent,
  } = revenue;

  const {
    data: earningsScenarioData,
    isLoading,
    isError,
    isRefetching,
  } = useEarningsScenario({
    ticker: entityData?.data?.ticker ?? '',
    exchange: exchange,
    year: fiscal_year,
    period: fiscal_period,
  });

  if (!entityData?.data) {
    return null;
  }

  const currentDate = new Date();
  const reportingDate = new Date(reporting_date);
  const shouldBeReported = currentDate >= reportingDate;
  const hasReportedValues = !!epsValue || !!revenueValue;

  const tableHeaders = [
    `(${currency})`,
    t('calendar:expected'),
    t('calendar:reported'),
    t('calendar:surpriseWithUnit', { unit: `(${currency})` }),
    t('calendar:surpriseWithUnit', { unit: '%' }),
  ];

  const renderValue = (value: number | undefined, isPercentage = false) => {
    return (
      <WithNAFallback
        value={formatNumber({ isPercentage, value })}
        check={value !== undefined}
      />
    );
  };

  const getSurpriseClass = (value: number | undefined) =>
    clsx(styles.surpriseWrapper, surpriseClassMap[getPriceStatus(value)]);

  const tableRows = [
    {
      fieldName: t('calendar:eps'),
      expected: renderValue(epsEstimate),
      reported: renderValue(epsValue),
      surprise: (
        <div className={getSurpriseClass(epsSurprise)}>
          {renderValue(epsSurprise)}
        </div>
      ),
      surprisePercent: (
        <div className={getSurpriseClass(epsSurprisePercentage)}>
          {renderValue(epsSurprisePercentage, true)}
        </div>
      ),
    },
    {
      fieldName: t('calendar:revenue'),
      expected: renderValue(revenueEstimate),
      reported: renderValue(revenueValue),
      surprise: (
        <div className={getSurpriseClass(revenueSurprise)}>
          {renderValue(revenueSurprise)}
        </div>
      ),
      surprisePercent: (
        <div className={getSurpriseClass(revenueSurprisePercent)}>
          {renderValue(revenueSurprisePercent, true)}
        </div>
      ),
    },
  ];

  const getChartContent = () => {
    if (isLoading || isRefetching) {
      return (
        <SkeletonLoader
          areas={[
            {
              styles: {
                width: '100%',
                height: '240px',
                borderRadius: '8px',
              },
            },
          ]}
        />
      );
    }

    if (isError || !earningsScenarioData) {
      return <ChartError />;
    }

    return (
      <EarningsWidget
        earningsData={earningsScenarioData}
        entity={entityData.data}
        source={source}
      />
    );
  };

  return (
    <div className={styles.earningsDrawerContentRoot}>
      {shouldBeReported && !hasReportedValues && (
        <Alert
          variant="inline"
          type={AlertType.Info}
          title={t('calendar:earningsBeingAnalyzed', {
            period: fiscal_period,
            year: fiscal_year % 100,
          })}
        />
      )}
      <div className={styles.styledSummaryTableWrapper}>
        <SummaryTable
          title={
            <div>
              {t('calendar:earningsSummary', {
                period: fiscal_period,
                year: fiscal_year,
              })}
            </div>
          }
          tableHeaders={tableHeaders}
          tableRows={tableRows}
        />
      </div>
      {getChartContent()}
    </div>
  );
};
